/**
 * OnboardingGuard — decides where the user lands based on their state.
 *
 * Flow:
 *   SignedOut           → LandingPage (no guard needed; RequireAuth handles in prod)
 *   SignedIn, no sub    → /redeem
 *   SignedIn, sub, players == 0 → /scenarios (forced)
 *   otherwise           → render children (AppShell + Dashboard)
 *
 * Runs *inside* the authenticated tree; it never redirects SignedOut users.
 */

import { useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { usePlayers } from "@/hooks/usePlayers";
import { Skeleton } from "@/components/ui";

// Paths that the guard ignores entirely (renders children immediately,
// no subscription / players queries gate them).
const BYPASS_PATHS = new Set([
  "/landing",
  "/redeem",
  "/scenarios",
  "/onboarding",
  "/players",
]);

export function OnboardingGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const bypass = BYPASS_PATHS.has(location.pathname);

  // Skip hooks for public paths — they may not even have a backend reachable.
  const sub = useSubscription();
  const players = usePlayers();

  const ready = sub.isSuccess && players.isSuccess;
  const hasSub = sub.data?.tier && sub.data.tier !== "free";
  const hasPlayer = (players.data?.length ?? 0) > 0;

  useEffect(() => {
    if (bypass || !ready) return;
    if (!hasSub) {
      void navigate("/redeem", { replace: true });
      return;
    }
    if (!hasPlayer) {
      void navigate("/players", { replace: true });
    }
  }, [bypass, ready, hasSub, hasPlayer, navigate]);

  if (bypass) return <>{children}</>;

  if (!ready) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return <>{children}</>;
}
